/**
 * Tenant licenses (TENANT-LICENSES).
 *
 * Issuers (platform admins; later a sales role) manage every license;
 * a tenant's admins read their own with `getTenantLicense` — null when the
 * tenant has none ("unlicensed": not limited).
 */
import { apiClient } from './client';

export type LicenseState = 'active' | 'grace' | 'read_only';
export type LimitLevel = 'ok' | 'warn' | 'over' | 'blocked';

export const LICENSE_TERMS_MONTHS = [3, 6, 12, 18, 24, 36] as const;
export const TRIAL_TERMS_DAYS = [7, 14, 21] as const;

export interface SubLicense {
  id: string;
  key: string;
  tenant_id: string;
  tenant_name: string | null;
}

export interface License {
  id: string;
  key: string;
  tenant_id: string;
  tenant_name: string | null;
  /** Set on a sub-license: the main license it follows. */
  main_key: string | null;
  main_tenant_name: string | null;
  kind: 'standard' | 'trial' | null;
  state: LicenseState;
  starts_at: string | null;
  ends_at: string | null;
  grace_ends_at: string | null;
  days_left: number | null;
  max_seats: number | null;
  max_devices: number | null;
  seats_used: number;
  devices_used: number;
  seat_level: LimitLevel;
  device_level: LimitLevel;
  /** Recorded procedures allowed per license term, and used since it began. */
  max_procedures: number | null;
  procedures_used: number;
  procedure_level: LimitLevel;
  term_started_at: string | null;
  subs: SubLicense[];
  revoked_at: string | null;
  /** Issuer-only (pricing, contacts); null for a tenant's admins. */
  notes: string | null;
}

export interface LicenseEvent {
  action: string;
  at: string;
  actor_user_id: string | null;
  detail: Record<string, unknown> | null;
}

export interface LicenseDetail extends License {
  events: LicenseEvent[];
}

export async function listLicenses(): Promise<License[]> {
  const { data } = await apiClient.get<License[]>('/api/v1/licenses');
  return data;
}

export async function getLicense(id: string): Promise<LicenseDetail> {
  const { data } = await apiClient.get<LicenseDetail>(`/api/v1/licenses/${id}`);
  return data;
}

export async function issueLicense(body: {
  tenant_id: string;
  months?: number;
  trial_days?: number;
  max_seats?: number | null;
  max_devices?: number | null;
  max_procedures?: number | null;
  notes?: string | null;
}): Promise<License> {
  const { data } = await apiClient.post<License>('/api/v1/licenses', body);
  return data;
}

export async function renewLicense(id: string, months: number): Promise<License> {
  const { data } = await apiClient.post<License>(`/api/v1/licenses/${id}/renew`, { months });
  return data;
}

export async function setLicenseLimits(
  id: string,
  limits: { max_seats: number | null; max_devices: number | null; max_procedures: number | null },
): Promise<License> {
  const { data } = await apiClient.patch<License>(`/api/v1/licenses/${id}/limits`, limits);
  return data;
}

export async function addSubLicense(id: string, tenantId: string): Promise<License> {
  const { data } = await apiClient.post<License>(`/api/v1/licenses/${id}/subs`, {
    tenant_id: tenantId,
  });
  return data;
}

export async function revokeLicense(id: string, reason: string | null): Promise<string[]> {
  const { data } = await apiClient.post<string[]>(`/api/v1/licenses/${id}/revoke`, { reason });
  return data;
}

export async function getTenantLicense(tenantId: string): Promise<License | null> {
  const { data } = await apiClient.get<License | null>(`/api/v1/tenants/${tenantId}/license`);
  return data;
}
